import {
  NEW_MESSAGE_SOUND,
  NEW_ORDER_SOUND,
} from "../assets/sounds/notificationSound";

const MIN_MS_BETWEEN_MESSAGE_SOUND_STARTS = 10 * 1000;
const AUDIO_UNLOCK_KEY = "kd_notif_audio_unlocked";
const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAE=";

const PUBLIC_SOUND_BASE = `${process.env.PUBLIC_URL || ""}/sounds`;
const PUBLIC_ORDER_SOUND = `${PUBLIC_SOUND_BASE}/new_order.mp3`;
const PUBLIC_MESSAGE_SOUND = `${PUBLIC_SOUND_BASE}/new_message.mp3`;

let messageAudioUnlocked = false;
let lastMessageSoundStartTime = 0;

function resolvePayloadTitle(payload) {
  const n = payload?.notification || {};
  const data = payload?.data || {};
  return String(n.title || data.title || "").trim();
}

function isBackgroundTabContext(fromBackground = false) {
  if (fromBackground) {
    return true;
  }
  if (typeof document === "undefined") {
    return false;
  }
  return document.visibilityState === "hidden" || document.hidden === true;
}

function resolveSoundTypeFromPayload(payload) {
  const data = payload?.data || {};
  const explicit = String(data.soundType || data.sound || "").toLowerCase();
  const pushKind = String(data.pushKind || "").toLowerCase();

  if (pushKind === "custom" || explicit === "default") {
    return "default";
  }
  if (explicit === "order" || explicit === "new_order") {
    return "order";
  }
  if (explicit === "message" || explicit === "new_message") {
    return "message";
  }

  const raw = resolvePayloadTitle(payload);
  const lower =
    typeof raw.toLocaleLowerCase === "function" ?
      raw.toLocaleLowerCase("tr-TR")
    : raw.toLowerCase();
  const asciiFold = lower
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");

  if (
    lower.includes("sipariş") ||
    lower.includes("siparis") ||
    asciiFold.includes("siparis") ||
    asciiFold.includes("yeni siparis")
  ) {
    return "order";
  }
  return "default";
}

export function resolveNotificationSoundSrc(
  payload,
  { preferPublicUrl = false } = {},
) {
  const soundType = resolveSoundTypeFromPayload(payload);
  if (soundType === "default") {
    return null;
  }

  if (preferPublicUrl && typeof window !== "undefined") {
    return soundType === "order" ? PUBLIC_ORDER_SOUND : PUBLIC_MESSAGE_SOUND;
  }

  return soundType === "order" ? NEW_ORDER_SOUND : NEW_MESSAGE_SOUND;
}

export function resolveNotificationSoundType(payload) {
  return resolveSoundTypeFromPayload(payload);
}

export function restoreFcmAudioUnlockFromSession() {
  try {
    if (sessionStorage.getItem(AUDIO_UNLOCK_KEY) === "1") {
      messageAudioUnlocked = true;
    }
  } catch {
    /* ignore */
  }
}

export function markFcmAudioUnlocked() {
  messageAudioUnlocked = true;
  try {
    sessionStorage.setItem(AUDIO_UNLOCK_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function tryUnlockFcmAudio() {
  if (messageAudioUnlocked || typeof Audio === "undefined") {
    return;
  }
  try {
    const audio = new Audio(SILENT_WAV);
    audio.volume = 0.0001;
    void audio
      .play()
      .then(() => {
        markFcmAudioUnlocked();
      })
      .catch(() => {});
  } catch {
    messageAudioUnlocked = false;
  }
}

export function requestBackgroundNotificationWithSound(payload) {
  if (
    typeof navigator === "undefined" ||
    !navigator.serviceWorker ||
    !payload
  ) {
    return;
  }
  void navigator.serviceWorker.ready
    .then((registration) => {
      registration.active?.postMessage({
        type: "show-linked-notification",
        payload,
      });
    })
    .catch(() => {});
}

export function playFcmNotificationSound(
  payload,
  { fromBackground = false } = {},
) {
  if (typeof Audio === "undefined" || !payload) {
    return false;
  }

  const backgroundContext = isBackgroundTabContext(fromBackground);
  const src = resolveNotificationSoundSrc(payload, {
    preferPublicUrl: backgroundContext,
  });
  if (!src) {
    return false;
  }

  const soundType = resolveSoundTypeFromPayload(payload);
  const isOrderSound = soundType === "order";

  if (!isOrderSound && !messageAudioUnlocked && !backgroundContext) {
    return false;
  }

  if (!isOrderSound) {
    const now = Date.now();
    if (
      lastMessageSoundStartTime > 0 &&
      now - lastMessageSoundStartTime < MIN_MS_BETWEEN_MESSAGE_SOUND_STARTS
    ) {
      return false;
    }
    lastMessageSoundStartTime = now;
  }

  try {
    const audio = new Audio(src);
    audio.volume = 0.85;
    void audio.play().catch((err) => {
      if (process.env.NODE_ENV === "development") {
        console.warn("FCM notification sound play rejected:", err);
      }
    });
    return true;
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.warn("notification sound error", e);
    }
    return false;
  }
}

export function playFcmNotificationSoundByType(soundType, payload = null) {
  if (soundType === "default") {
    return false;
  }

  const syntheticPayload =
    payload ||
    (soundType === "order" ?
      { data: { soundType: "order" }, notification: { title: "Yeni Sipariş" } }
    : {
        data: { soundType: "message" },
        notification: { title: "Yeni mesaj" },
      });

  return playFcmNotificationSound(syntheticPayload, {
    fromBackground: true,
  });
}

export function handleBackgroundFcmPayload(payload) {
  if (!payload) {
    return;
  }
  requestBackgroundNotificationWithSound(payload);
}
